/*---------------------------------------------------------------------------------------------*
 * Summary                                                                                     *
 *---------------------------------------------------------------------------------------------*
 
 Contains calculations for graphing a duval triangle.

 Usage:
   import { createCalculator } from './calculations.js';
   ...
   const calculations = createCalculator({ initOptions });
   calculations.gasPPMtoCoordinateString(ppm);

 */

import { compose, curry } from 'ramda';

/*---------------------------------------------------------------------------------------------*
 * Triangle Conversions                                                                        *
 *---------------------------------------------------------------------------------------------*/
const convertPPMtoPercentage = (ppmValues) => {
  const denominator = ppmValues.ch4 + ppmValues.c2h4 + ppmValues.c2h2;
  return {
    ch4: (100 * ppmValues.ch4) / denominator,
    c2h4: (100 * ppmValues.c2h4) / denominator,
    c2h2: (100 * ppmValues.c2h2) / denominator
  };
};

const convertPercentageToCoordinates = (percentageValues) => ({
  x: 0 + percentageValues.ch4 * Math.cos(30 * (Math.PI / 180)),
  y: 0 + (percentageValues.c2h4 + percentageValues.ch4 * 0.5)
});

/*---------------------------------------------------------------------------------------------*
 * Helpers                                                                                     *
 *---------------------------------------------------------------------------------------------*/
const coordinatesToString = (coordinates) => `${coordinates.x},${coordinates.y}`;
const xCoordinatesToString = (coordinates) => `${coordinates.x}`;
const yCoordinatesToString = (coordinates) => `${coordinates.y}`;

const applyXScale = curry((scale, coordinates) => {
  return {
    ...coordinates,
    x: scale ? scale(coordinates.x) : coordinates.x
  };
});

const applyYScale = curry((scale, coordinates) => {
  return {
    ...coordinates,
    y: scale ? scale(coordinates.y) : coordinates.y
  };
});

const normalizeGasPropertyNames = (gases) => ({
  ...gases,
  c2h2: gases.c2H2 === undefined ? gases.c2h2 : gases.c2H2,
  c2h4: gases.c2H4 === undefined ? gases.c2h4 : gases.c2H4,
  c2h6: gases.c2H6 === undefined ? gases.c2h6 : gases.c2H6,
  c3h6: gases.c3H6 === undefined ? gases.c3h6 : gases.c3H6,
  c3h8: gases.c3H8 === undefined ? gases.c3h8 : gases.c3H8,
  ch4: gases.cH4 === undefined ? gases.ch4 : gases.cH4,
  co2: gases.cO2 === undefined ? gases.co2 : gases.cO2
});

const checkGasValues = (gases) => {
  return !(
    gases.acetylene === 0 &&
    gases.c2h2 === 0 &&
    gases.c2h4 === 0 &&
    gases.c2h6 === 0 &&
    gases.c3h6 === 0 &&
    gases.c3h8 === 0 &&
    gases.ch4 === 0 &&
    gases.co2 === 0 &&
    gases.co === 0 &&
    gases.h2 === 0 &&
    gases.n2 === 0 &&
    gases.o2 === 0
  );
};

/*---------------------------------------------------------------------------------------------*
 * Exposed Functions                                                                           *
 *---------------------------------------------------------------------------------------------*/
export function createCalculator({ xScale, yScale }) {
  const gasPPMtoCoordinates = compose(
    applyXScale(xScale),
    applyYScale(yScale),
    convertPercentageToCoordinates,
    convertPPMtoPercentage,
    normalizeGasPropertyNames
  );
  return {
    gasPPMtoCoordinateString: compose(coordinatesToString, gasPPMtoCoordinates),
    gasPPMtoXcoordinateString: compose(xCoordinatesToString, gasPPMtoCoordinates),
    gasPPMtoYcoordinateString: compose(yCoordinatesToString, gasPPMtoCoordinates),
    gasPercentageToCoordinateString: compose(
      coordinatesToString,
      applyXScale(xScale),
      applyYScale(yScale),
      convertPercentageToCoordinates,
      normalizeGasPropertyNames
    ),
    gasPercentageToXCoordinateString: compose(
      xCoordinatesToString,
      applyXScale(xScale),
      convertPercentageToCoordinates,
      normalizeGasPropertyNames
    ),
    gasPercentageToYCoordinateString: compose(
      yCoordinatesToString,
      applyYScale(yScale),
      convertPercentageToCoordinates,
      normalizeGasPropertyNames
    )
  };
}

export const hasGasValues = compose(checkGasValues, normalizeGasPropertyNames);
